/**
 * DEMO-ONLY HTTP handlers for portal device labels + work-item create/update.
 * Work-item delete is retired (approved exception to MVP 6.3.11).
 */
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import {
  CreateWorkItemPayload,
  DeviceAsset,
  PortalListResponse,
  WorkItem,
} from '../../presidio-portal/model/presidio-portal.model';

import { getAdminAffiliateContractDetail } from './presidio-mock-admin-my-account';
import {
  applyPresidioMockDeviceLabelOverlay,
  buildPresidioMockCreatedWorkItem,
  buildPresidioMockUpdatedWorkItem,
  mergePresidioMockWorkItems,
  persistPresidioMockCreatedWorkItem,
  readPresidioMockCreatedWorkItems,
  writePresidioMockDeviceLabels,
} from './presidio-mock-portal-state';

const DEVICE_LABELS = /^customers\/[^/]+\/devices\/([^/]+)\/labels$/i;
const DEVICES_LIST = /^customers\/[^/]+\/devices$/i;
const CONTRACT_ONE = /^customers\/[^/]+\/contracts\/([^/]+)$/i;
const WORKITEMS_LIST = /^customers\/[^/]+\/workitems$/i;
const WORKITEM_ONE = /^customers\/[^/]+\/workitems\/([^/]+)$/i;

/** Short-circuit mutations / created-detail GETs (no static fixture required). */
export function handlePresidioMockPortalMutation(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T
): Observable<HttpEvent<unknown>> | null {
  const method = req.method.toUpperCase();

  const labelsMatch = DEVICE_LABELS.exec(restPath);
  if (labelsMatch && method === 'PUT') {
    const deviceId = decodeURIComponent(labelsMatch[1]);
    const body = readJsonBody<{ labels?: string[] }>(req.body);
    const labels = Array.isArray(body?.labels) ? body.labels.map(l => String(l).trim()).filter(Boolean) : [];
    writePresidioMockDeviceLabels(deviceId, labels);
    return of(new HttpResponse({ status: 200, body: { id: deviceId, labels }, url: req.url }));
  }

  if (WORKITEMS_LIST.test(restPath) && method === 'POST') {
    const payload = readJsonBody<CreateWorkItemPayload>(req.body) || { type: 'General Inquiry' };
    // Contract integration assigns work item # at submit; MCS is emailed (demo audit).
    const item = buildPresidioMockCreatedWorkItem({ ...payload, status: 'Pending Presidio' });
    persistPresidioMockCreatedWorkItem(item);
    return of(new HttpResponse({ status: 201, body: item, url: req.url }));
  }

  // Affiliate contracts (MC-BETA-1 / MC-GAMMA-1) are list-overlay only — no static get.json.
  const contractMatch = CONTRACT_ONE.exec(restPath);
  if (contractMatch && method === 'GET') {
    const contractId = decodeURIComponent(contractMatch[1]);
    const detail = getAdminAffiliateContractDetail(contractId);
    if (detail) {
      return of(new HttpResponse({ status: 200, body: detail, url: req.url }));
    }
  }

  const wiMatch = WORKITEM_ONE.exec(restPath);
  if (wiMatch && method === 'PUT') {
    const id = decodeURIComponent(wiMatch[1]);
    const payload = readJsonBody<CreateWorkItemPayload>(req.body) || { type: 'General Inquiry' };
    const existing = readPresidioMockCreatedWorkItems().find(w => w.id === id) || {
      id,
      number: payload.number || id,
      type: payload.type || 'General Inquiry',
      requester: 'Jordan Hale',
      status: 'Pending Presidio' as const,
      submittedDate: new Date().toISOString(),
      createdDate: new Date().toISOString(),
      comments: '',
      audit: [],
    };
    const updated = buildPresidioMockUpdatedWorkItem(existing, payload);
    persistPresidioMockCreatedWorkItem(updated);
    return of(new HttpResponse({ status: 200, body: updated, url: req.url }));
  }

  if (wiMatch && method === 'DELETE') {
    return of(
      new HttpResponse({
        status: 405,
        body: { errors: [{ message: 'Work item delete is not supported' }] },
        url: req.url,
      })
    );
  }

  if (wiMatch && method === 'GET') {
    const id = decodeURIComponent(wiMatch[1]);
    const created = readPresidioMockCreatedWorkItems().find(w => w.id === id);
    if (created) {
      return of(new HttpResponse({ status: 200, body: created, url: req.url }));
    }
  }

  return null;
}

/** Overlay static GET list fixtures with session labels / created / deleted work items. */
export function applyPresidioMockPortalListOverlay(restPath: string, method: string, body: unknown): unknown {
  if (method.toUpperCase() !== 'GET' || !body || typeof body !== 'object') {
    return body;
  }
  if (DEVICES_LIST.test(restPath) && 'elements' in (body as object)) {
    const list = body as PortalListResponse<DeviceAsset>;
    const elements = applyPresidioMockDeviceLabelOverlay(list.elements || []);
    return { ...list, elements, total: elements.length };
  }
  if (WORKITEMS_LIST.test(restPath) && 'elements' in (body as object)) {
    const list = body as PortalListResponse<WorkItem>;
    const elements = mergePresidioMockWorkItems(list.elements || []);
    return { ...list, elements, total: elements.length };
  }
  return body;
}
